import { IconBellOff, IconEye } from '@tabler/icons-react'
import React from 'react'

import { AlertBadge, ScalrEmptyBox } from '@/components/customComponents'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import {
  markAlertAsRead,
  selectAlertsByHierarchy,
  useAppDispatch,
  useAppSelector,
} from '@/store'
import { AlertHierarchy } from '@/types/alerts'
import { cn } from '@/utils'

interface DetailedNotificationBoxProps {
  /**
   * The hierarchy to filter alerts for this specific item
   */
  hierarchy: Partial<AlertHierarchy>
  /**
   * Optional className for styling
   */
  className?: string
  /**
   * Optional title for the notification box
   */
  title?: string
  /**
   * Compact mode for smaller display
   */
  compact?: boolean
}

export const DetailedNotificationBox: React.FC<
  DetailedNotificationBoxProps
> = ({ hierarchy, className, title = 'Item Alerts', compact = false }) => {
  const dispatch = useAppDispatch()
  const itemAlerts = useAppSelector(selectAlertsByHierarchy(hierarchy))
  const unreadCount = itemAlerts.filter((alert) => !alert.isRead).length

  const handleViewAlerts = () => {
    itemAlerts.forEach((alert) => {
      if (!alert.isRead) {
        dispatch(markAlertAsRead(alert.id))
      }
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  if (compact) {
    return (
      <div
        className={cn('border border-border rounded-lg bg-card p-3', className)}
      >
        {itemAlerts.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <IconBellOff className="h-4 w-4" />
            <span>No alerts</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {title}
                </span>
              </div>
              {unreadCount > 0 && <AlertBadge unreadAlerts={unreadCount} />}
            </div>
            <div className="text-xs text-muted-foreground">
              {itemAlerts.length} alert{itemAlerts.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn('border border-border rounded-xl bg-card p-4', className)}
    >
      {itemAlerts.length === 0 ? (
        <ScalrEmptyBox
          title="No Alerts"
          message="There are currently no alerts for this item."
          icon={({ size, className }) => (
            <IconBellOff size={size} className={className} />
          )}
          size="sm"
          className="py-6"
        />
      ) : (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-medium text-foreground leading-none tracking-tight">
                {title}
              </span>
              {unreadCount > 0 && <AlertBadge unreadAlerts={unreadCount} />}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewAlerts}
              className="flex items-center gap-1 rounded-lg"
            >
              View <IconEye className="h-4 w-4" />
            </Button>
          </div>

          {/* Alerts List */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {itemAlerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  'border-l-2 pl-4 py-2 transition-colors',
                  alert.isRead ? 'border-muted' : 'border-destructive'
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  {alert.hierarchy.tab && (
                    <Badge
                      variant="outline"
                      className="text-xs px-2 py-1 rounded-md"
                    >
                      {alert.hierarchy.tab}
                    </Badge>
                  )}
                  <div className="text-xs text-muted-foreground mr-2">
                    {formatDate(alert.timestamp)}
                  </div>
                </div>
                <div
                  className={cn(
                    'text-sm leading-relaxed',
                    alert.isRead ? 'text-muted-foreground' : 'text-foreground'
                  )}
                >
                  {alert.message}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
